import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/billing.py schemas.
export interface Balance {
  usr_id: number;
  username: string;
  credit: number;
}

export interface Payment {
  payment_id: number;
  usr_id: number;
  username: string | null;
  amount: number;
  description: string | null;
  type: string | null;
  paid_date: string | null;
}

export interface CardPaymentConfig {
  enabled: boolean;
  sandbox: boolean;
  api_login_id: string;
  public_client_key: string;
  min_amount: number;
  max_amount: number;
}

export interface OpaqueData {
  dataDescriptor: string;
  dataValue: string;
}

export interface CardChargeInput {
  opaqueData: OpaqueData;
  amount: number;
  idempotency_key: string;
  cardholder_name?: string;
  billing_zip?: string;
}

export interface CardChargeResult {
  ok: boolean;
  declined?: boolean;
  pending?: boolean;
  duplicate?: boolean;
  trans_id?: string | null;
  response_code?: string | null;
  amount_credited?: number | null;
  new_balance?: number | null;
  card_summary?: string | null;
  message: string;
}

export async function listBalances(): Promise<Balance[]> {
  const resp = await api.get<APIResponse<Balance[]>>("/billing/balances");
  return unwrap(resp.data);
}

export async function listPayments(): Promise<Payment[]> {
  const resp = await api.get<APIResponse<Payment[]>>("/billing/payments");
  return unwrap(resp.data);
}

export async function getCardPaymentConfig(): Promise<CardPaymentConfig> {
  const resp = await api.get<APIResponse<CardPaymentConfig>>("/billing/authorize-net/config");
  return unwrap(resp.data);
}

export async function chargeCardPayment(body: CardChargeInput): Promise<CardChargeResult> {
  const resp = await api.post<APIResponse<CardChargeResult>>("/billing/authorize-net/charge", body);
  return unwrap(resp.data);
}

/** Format the expiry field as the user types: insert "/" after the 2-digit month. */
export function formatExpiryInput(raw: string): string {
  const cleaned = raw.replace(/[^\d/]/g, "");
  const slash = cleaned.indexOf("/");
  if (slash !== -1) {
    const month = cleaned.slice(0, slash).replace(/\D/g, "").slice(0, 2);
    const year = cleaned.slice(slash + 1).replace(/\D/g, "").slice(0, 4);
    return `${month}/${year}`;
  }
  if (cleaned[0] >= "2" && cleaned[0] <= "9") {
    const year = cleaned.slice(1, 5);
    return year ? `0${cleaned[0]}/${year}` : `0${cleaned[0]}`;
  }
  if (cleaned.length <= 2) return cleaned;
  return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 6)}`;
}

/** Parse MM/YY or MM/YYYY into zero-padded month + 2/4-digit year for Accept.js. */
export function parseExpiry(raw: string): { month: string; year: string } | null {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;

  let month: string;
  let year: string;
  const slash = trimmed.indexOf("/");
  if (slash !== -1) {
    month = trimmed.slice(0, slash).replace(/\D/g, "");
    year = trimmed.slice(slash + 1).replace(/\D/g, "");
  } else {
    const digits = trimmed.replace(/\D/g, "");
    if (digits[0] >= "2" && digits[0] <= "9") {
      month = digits.slice(0, 1);
      year = digits.slice(1);
    } else {
      month = digits.slice(0, 2);
      year = digits.slice(2);
    }
  }

  if (month.length === 1) month = `0${month}`;
  if (month.length !== 2) return null;

  const m = Number(month);
  if (!Number.isInteger(m) || m < 1 || m > 12) return null;
  if (year.length !== 2 && year.length !== 4) return null;

  return { month, year };
}
